import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  onMounted,
  onUnmounted,
  provide,
  ref,
  nextTick
} from 'vue';
import { onBeforeRouteUpdate, useRouter } from 'vue2-helpers/vue-router';
import { useStore } from 'vue2-helpers/vuex';
import ProjectForumTopic from '@/views/project-forum/components/ProjectForumTopic.vue';
import ThreadReplies from '@/views/forum/components/ThreadReplies.vue';
import BCXNewStuffBanner from '@/components/molecules/BCXNewStuffBanner.vue';
import { ForumMaxChars, MessagePost } from '@/views/forum/models/Forum';
import ForumService from '@/services/ForumService';
import { PostSubscription } from '@/views/subscriptions/models/Subscription';
import useIsLeavingDirty from '@/utils/isLeavingDirty';
import BCXConfirm from '@/components/molecules/BCXConfirm.vue';
import useUser from '@/mixins/useUser';
import usePageState from '@/state/pageState';
import ProjectOffersListBox from '@/views/project-forum/components/sidebar-actions/ProjectOffersListBox.vue';
import useResponsiveness from '@/mixins/useResponsiveness';
import MarvinInfoBox from '@/views/recommendations/components/MarvinInfoBox.vue';
import { logError } from '@/utils/logger';
import useInfoPopupState from '@/state/infoPopupState';

// Provide

export default /*#__PURE__*/_defineComponent({
  __name: 'ProjectForumThread',
  setup(__props) {

provide('maxCreateMessageTextLength', ForumMaxChars.PROJECT_REPLY);

// Store and router
const store = useStore();
const router = useRouter();

// Composables
const { showInfoPopupIfUnread } = useInfoPopupState();
const { tUserFallback: t } = useUser('self');
const { isSidebarVisible } = useResponsiveness();
const {
  isDirty,
  isLeavingDirty,
  isLeavingMessage,
  onConfirm
} = useIsLeavingDirty();

// Refs
const newMessagesAvailable = ref(false);
const intervalSeconds = ref(60);
const numberOfReplies = ref(0);
const numberOfEdits = ref(0);

// Computed from store
const user = computed(() => store.getters['user']);
const project = computed(() => store.state.projectForum.project);
const thread = computed(() => store.state.projectForum.thread);
const messagesSort = computed(() => store.state.projectForum.messagesSort);

const getThreadRepliesTotal = computed(() => store.getters['projectForum/getThreadRepliesTotal']);
const getThreadRepliesSorted = computed(() => store.getters['projectForum/getThreadRepliesSorted']);
const getOpenEditMessageId = computed(() => store.getters['projectForum/getOpenEditMessageId']);
const getSubscriptionsByObjectId = computed(() => store.getters['subscriptions/getSubscriptionsByObjectId']);

// Computed properties
const isProjectClosed = computed(() => project.value.status !== 'ACTIVE' && project.value.status !== 'HOLD');
const threadSubscribed = computed(() => thread.value.forumThreadMetaData?.userSubscribed
  || Object.keys(getSubscriptionsByObjectId.value).includes(project.value.id));
const isTestProject = computed(() => project.value.metaDataProject?.testFlag || false);
const userIsCreator = computed(() => user.value?.userId === project.value?.creator?.userId);

// Set dirty state
const setIsDirty = (value: boolean) => {
  isDirty.value = value;
};

const pageNotFound = () => {
  const { pageNotFound } = usePageState();
  pageNotFound.value = true;
  router.push('/projects');
};
const clearProjectAndThread = () => store.dispatch('projectForum/clearProjectAndThread');
const fetchProject = (projectId: string) => store.dispatch('projectForum/fetchProject', projectId);
const fetchProjectThread = (projectId: string) => store.dispatch('projectForum/fetchProjectThread', projectId);
const postThreadMessage = (postMessage: MessagePost) => store.dispatch('projectForum/postThreadMessage', postMessage);
const updateThreadMessage = (payload: any) => store.dispatch('projectForum/updateThreadMessage', payload);
const editThreadMessage = (messageId: string) => store.dispatch('projectForum/editThreadMessage', messageId);
const sortThreadReplies = (payload: any) => store.dispatch('projectForum/sortThreadReplies', payload);
const refreshThread = () => store.dispatch('projectForum/refreshThread');
const postSubscription = (data: PostSubscription) => store.dispatch('subscriptions/postSubscription', data);
const deleteSubscription = (subscriptionId: string) => store.dispatch('subscriptions/deleteSubscription', subscriptionId);
const fetchSubscriptions = () => store.dispatch('subscriptions/fetchSubscriptions');
const showProjectQuestionsHowto = () => showInfoPopupIfUnread({
  component: 'InfoPopup_ProjectQuestions',
  context: 'project_questions_howto',
  value: '1',
  visibleForRoles: {
    FREELANCER: true,
    COMPANY_ROOT: true,
    COMPANY_GUEST: true,
  },
});

const updateThreadMeta = (compare = false) => {
  const { threadId, forumId, groupId } = thread.value;
  if (!forumId || !groupId || !threadId) {
    return;
  }

  ForumService.getThreadMeta(forumId, groupId, threadId).then((threadMeta) => {
    if (compare) {
      const sumOfActionsMeta = (threadMeta.numberOfReplies ?? 0) + (threadMeta.numberOfEdits ?? 0);
      if (sumOfActionsMeta > (numberOfReplies.value + numberOfEdits.value)) {
        newMessagesAvailable.value = true;
      }
    }
    numberOfReplies.value = threadMeta.numberOfReplies;
    numberOfEdits.value = threadMeta.numberOfEdits;
  }).catch((error) => {
    if (error?.response?.status === 404) {
      return;
    }
    logError(error);
  });
};

// Navigation guard
onBeforeRouteUpdate((to, _from, next) => {
  clearProjectAndThread();
  const { projectId } = to.params;
  fetchProject(projectId).then(() => {
    fetchProjectThread(projectId).then(() => {
      updateThreadMeta();
      next();
      nextTick(() => {
        showProjectQuestionsHowto();
      });
    }).catch(() => {
      pageNotFound();
    });
  });
});

// Lifecycle hooks
onMounted(() => {
  const { projectId } = router.currentRoute.params;
  fetchProject(projectId).then(() => {
    store.dispatch('subscriptions/fetchSubscriptions');
    fetchProjectThread(projectId).then((forum: any) => {
      const threadsViewed = JSON.parse(window.localStorage.getItem('threadsViewed') || '{}');
      const { threadId, forumId, groupId } = forum.groups[0].threads[0];
      const viewTimestamp = threadsViewed[threadId] || '';
      const currentTimestamp: number = (new Date()).getTime();
      if (viewTimestamp === undefined || viewTimestamp + 30 * 60000 < currentTimestamp) {
        ForumService.trackPageViewThread(forumId, threadId, groupId);
        threadsViewed[threadId] = currentTimestamp;
        window.localStorage.setItem('threadsViewed', JSON.stringify(threadsViewed));
      }

      showProjectQuestionsHowto();

      nextTick(() => {
        updateThreadMeta();
      });
    }).catch(() => pageNotFound());
  }).catch(() => pageNotFound());

  window.threadRefreshInterval = setInterval(() => {
    updateThreadMeta(true);
  }, intervalSeconds.value * 1000);
});

onUnmounted(() => {
  clearInterval(window.threadRefreshInterval);
  clearProjectAndThread();
});

// Methods
const loadNewMessages = () => {
  refreshThread().then(() => {
    newMessagesAvailable.value = false;
  });
};
const closeNewMessagesBanner = () => {
  newMessagesAvailable.value = false;
  intervalSeconds.value = Math.round(intervalSeconds.value * 1.25);
};

const postMessage = (postMessage: MessagePost) => {
  postThreadMessage(postMessage).then(() => {
    fetchSubscriptions();
    setIsDirty(false);
  });
  numberOfReplies.value++;
};

const updateMessage = (postMessage: MessagePost, messageCount: string) => {
  updateThreadMessage({ postMessage, messageCount });
  numberOfEdits.value++;
};

const editMessage = (editMessageId: string) => {
  if (getOpenEditMessageId.value === editMessageId) {
    editThreadMessage('');
  } else {
    editThreadMessage(editMessageId);
  }
};

const sortReplies = (sortParam: string, sortOrder: string) => {
  sortThreadReplies({ sortParam, sortOrder });
};

const subscribeToProject = () => {
  if (!threadSubscribed.value) {
    const postSubscriptionData: PostSubscription = {
      businessObjectId: project.value.id,
      subscriptionType: 'PROJECT',
    };
    postSubscription(postSubscriptionData);
  }
};

const unsubscribeFromProject = () => {
  if (Object.keys(getSubscriptionsByObjectId.value).length > 0) {
    const subscription = getSubscriptionsByObjectId.value[project.value.id];
    if (subscription) {
      const { subscriptionId } = subscription;
      deleteSubscription(subscriptionId);
    }
  }
};

const increaseNumberOfReplies = () => {
  numberOfReplies.value++;
};


return { __sfc: true,store, router, showInfoPopupIfUnread, t, isSidebarVisible, isDirty, isLeavingDirty, isLeavingMessage, onConfirm, newMessagesAvailable, intervalSeconds, numberOfReplies, numberOfEdits, user, project, thread, messagesSort, getThreadRepliesTotal, getThreadRepliesSorted, getOpenEditMessageId, getSubscriptionsByObjectId, isProjectClosed, threadSubscribed, isTestProject, userIsCreator, setIsDirty, pageNotFound, clearProjectAndThread, fetchProject, fetchProjectThread, postThreadMessage, updateThreadMessage, editThreadMessage, sortThreadReplies, refreshThread, postSubscription, deleteSubscription, fetchSubscriptions, showProjectQuestionsHowto, updateThreadMeta, loadNewMessages, closeNewMessagesBanner, postMessage, updateMessage, editMessage, sortReplies, subscribeToProject, unsubscribeFromProject, increaseNumberOfReplies, ProjectForumTopic, ThreadReplies, BCXNewStuffBanner, BCXConfirm, ProjectOffersListBox, MarvinInfoBox }
}

})